// Single source for the payments profile: one GET /me per session, shared
// by the per-agent chips and the Billing page; refetch on window focus so a
// deep-link round-trip to the marketplace shows fresh balances on return.
// Reads degrade ('unavailable'), they never gate browsing or runs (spec §5/§9).
//
// Reads a wallet-api delegation BEARER, never a live signature — the same
// reasoning as lib/clients/vault/read-client.ts: after a reload the wallet is
// keyless (`resume()` restores the bearer session, never the PRF signing
// keys), so a signed boot read would demand a step-up popup with no user
// gesture to open it. `requestDelegation` needs only an authenticated
// session (measured live: ~170ms, no popup, no keys).

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { getPaymentsProfile, PaymentsError, type PaymentsProfile } from '@/src/marketplace/payments';
import { useVaultSession } from '@/src/state/session';
import { useConfig } from '@/src/config/context';

export type PaymentsProfileStatus = 'disabled' | 'loading' | 'ready' | 'unavailable';

export interface PaymentsProfileState {
  status: PaymentsProfileStatus;
  profile: PaymentsProfile | null;
  refetch: () => void;
  /**
   * Mint (or reuse a cached) delegation bearer for payments reads. Shared
   * with useBilling's ledger pagination so both read paths use the same
   * token source instead of each minting its own. `force: true` re-mints
   * even if the cache looks fresh — used after a 401.
   */
  getToken: (opts?: { force?: boolean }) => Promise<string>;
}

const DISABLED: PaymentsProfileState = {
  status: 'disabled',
  profile: null,
  refetch: () => {},
  getToken: () => Promise.reject(new Error('payments disabled: no PaymentsProfileProvider mounted')),
};

const Ctx = createContext<PaymentsProfileState | null>(null);

// Proven mintable against dev wallet-api (measured live: resolves ~170ms,
// no popup, no signing keys required).
const TOKEN_CAPABILITIES = ['ddc-read'] as const;
const TOKEN_TTL = '1h';
// Re-mint once the cached token is within this much of its expiry.
const REFRESH_MARGIN_MS = 5 * 60 * 1000;

/**
 * Run `run(token)`, and if it fails with a 401 re-mint the token once (force)
 * and retry — the cheap recovery for a cached token that expired since it
 * was minted. Shared by the profile fetch here and useBilling's ledger reads.
 */
export async function withTokenRetry<T>(
  getToken: PaymentsProfileState['getToken'],
  run: (token: string) => Promise<T>,
): Promise<T> {
  const token = await getToken();
  try {
    return await run(token);
  } catch (err) {
    if (err instanceof PaymentsError && err.status === 401) {
      return run(await getToken({ force: true }));
    }
    throw err;
  }
}

export function PaymentsProfileProvider({ children }: { children: ReactNode }) {
  const { wallet } = useVaultSession();
  const config = useConfig();
  const [status, setStatus] = useState<PaymentsProfileStatus>('loading');
  const [profile, setProfile] = useState<PaymentsProfile | null>(null);
  const inflight = useRef(false);
  // ponytail: lazy mint-on-use cache, no refresh scheduler — a stale-but-
  // uncaught-here token just 401s once and self-heals via withTokenRetry.
  const cachedRef = useRef<{ token: string; expiresAtMs: number | null } | null>(null);
  const mintingRef = useRef<Promise<string> | null>(null);

  const getToken = useCallback(async (opts?: { force?: boolean }): Promise<string> => {
    const cached = cachedRef.current;
    const fresh = cached !== null
      && (cached.expiresAtMs === null || cached.expiresAtMs - Date.now() > REFRESH_MARGIN_MS);
    if (!opts?.force && fresh) return cached.token;
    if (!opts?.force && mintingRef.current) return mintingRef.current;

    const minting = wallet.requestDelegation({
      capabilities: [...TOKEN_CAPABILITIES],
      appId: 'vault',
      ttl: TOKEN_TTL,
    }).then((d) => {
      // Guard against a non-ISO `expiresAt`: `Date.parse` on garbage input
      // yields NaN, not null, and `NaN - Date.now() > REFRESH_MARGIN_MS` is
      // always false — that reads as "never fresh", silently disabling the
      // cache forever with no symptom. Same guard as
      // lib/clients/vault/read-client.ts's `readTokenRefreshDelayMs`
      // (`!Number.isFinite(remainingMs)`), reused here for the same reason.
      const parsedMs = d.expiresAt ? Date.parse(d.expiresAt) : NaN;
      cachedRef.current = { token: d.token, expiresAtMs: Number.isFinite(parsedMs) ? parsedMs : null };
      mintingRef.current = null;
      return d.token;
    }).catch((err: unknown) => {
      mintingRef.current = null;
      throw err;
    });
    mintingRef.current = minting;
    return minting;
  }, [wallet]);

  const refetch = useCallback(() => {
    if (inflight.current) return;
    inflight.current = true;
    withTokenRetry(getToken, (token) => getPaymentsProfile(config.marketplaceBaseUrl, token))
      .then((p) => { setProfile(p); setStatus('ready'); })
      .catch((err: unknown) => {
        // Keep the failure reason visible instead of swallowing it — a
        // step-up/auth failure should not look identical to a plain outage.
        console.error('payments profile fetch failed', err);
        setStatus((s) => (s === 'ready' ? s : 'unavailable'));
      })
      .finally(() => { inflight.current = false; });
  }, [config.marketplaceBaseUrl, getToken]);

  useEffect(() => {
    refetch();
    window.addEventListener('focus', refetch);
    return () => window.removeEventListener('focus', refetch);
  }, [refetch]);

  const value = useMemo(
    () => ({ status, profile, refetch, getToken }),
    [status, profile, refetch, getToken],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePaymentsProfile(): PaymentsProfileState {
  return useContext(Ctx) ?? DISABLED;
}
